'use client';
import React, { ReactNode, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setSidenavVisible } from '@/lib/slices/nav-slice';
import { setAdminStudentView } from '@/lib/slices/course-slice';
import { setUnitId } from '@/lib/slices/unit-slice';
import { setSubunitId } from '@/lib/slices/subunit-slice';
import { resetActivitySlice } from '@/lib/slices/activity-slice';

import { BreadcrumbNav } from './topnav/BreadcrumbNav';
import { CourseProgressInfo } from './topnav/CourseProgressInfo';
import { AdminStudentViewToggle } from './topnav/AdminStudentViewToggle';
import { AdminCoursePageInfo } from './topnav/admin/AdminCoursePageInfo';
import { StudentActivityInfo } from './topnav/student/StudentActivityInfo';

import UserCourseStorageService from '@/services/storage/user-course-storage-service';
import UtilityService from '@/services/util/utility-service';
import ActivityUtilityService from '@/services/course/lesson/activity/activity-utility-service';
import { ActivityData, UnitData } from 'course-types';
import { role } from 'auth-types';

function TopNav(): React.JSX.Element | null {
  const pathName = usePathname();
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const [isCompleted, setIsCompleted] = useState(false);
  const [units, setUnits] = useState<Array<[string, UnitData]>>([]);
  const [isMobile, setIsMobile] = useState(false);

  const userRole: role = useAppSelector(() => store.getState().isLoggedIn.role);
  const { darkMode, visible } = useAppSelector(() => store.getState().navigation);
  const activity: ActivityData = useAppSelector(() => store.getState().activity);
  const {
    activeCourseData: course,
    courseList,
    adminStudentView,
  } = useAppSelector(() => store.getState().course);
  const unitId = useAppSelector(() => store.getState().unit.id);
  const subunitId = useAppSelector(() => store.getState().subunit.id);
  const quizJustSubmitted = useAppSelector(() => store.getState().quiz.justSubmitted);

  const isAdminCoursesRoot = pathName === '/admin/courses' || pathName === '/admin/courses/';
  const isStudentCourseRoot = pathName.startsWith('/student/courses');
  const isOnStudentPages =
    (pathName.startsWith('/student/course') || pathName.startsWith('/student/lesson')) &&
    !isStudentCourseRoot;
  const showGradient = isOnStudentPages || isAdminCoursesRoot;

  // Fetch course units data when course changes
  useEffect(() => {
    if (!course.id) return;

    const fetchUnits = () => {
      try {
        const courseData = UserCourseStorageService.getCourseData();
        if (courseData && courseData.units) {
          const unitsArray = Object.entries(courseData.units);
          setUnits(unitsArray);
        }
      } catch (error) {
        console.error('Error fetching course units:', error);
      }
    };

    fetchUnits();
  }, [course.id]);

  useEffect(() => {
    if (!activity.id) return;

    try {
      const { activityData } = ActivityUtilityService.retrieveActivityProgressLocal(
        course.id,
        unitId,
        subunitId,
      );
      if (activity.type === 'video') {
        setIsCompleted(!!activityData.video[activity.id]?.submissionDate);
      } else if (activity.type === 'document') {
        setIsCompleted(!!activityData.document[activity.id]?.submissionDate);
      } else if (activity.type === 'assignment') {
        setIsCompleted(!!activityData.assignment?.[activity.id]?.submissionDate);
      } else if (activity.type === 'quiz') {
        // For quizzes, use justSubmitted flag to show button only on results screen
        setIsCompleted(quizJustSubmitted);
      }
    } catch {
      setIsCompleted(false);
    }
  }, [activity, quizJustSubmitted]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Ensure admin student view resets when leaving admin courses root
  useEffect(() => {
    if (!isAdminCoursesRoot && adminStudentView) {
      dispatch(setAdminStudentView(false));
      dispatch(resetActivitySlice());
      dispatch(setSubunitId(undefined));
      dispatch(setUnitId(''));
    }
  }, [pathName]);

  const toggleSidenav = () => {
    dispatch(setSidenavVisible(!visible));
  };

  const renderContent = (): ReactNode => {
    if (isAdminCoursesRoot) {
      return !adminStudentView ? (
        <AdminCoursePageInfo
          course={course}
          courseList={courseList}
          userRole={userRole}
          adminStudentView={adminStudentView}
        />
      ) : activity.id ? (
        <StudentActivityInfo
          activity={activity}
          courseId={course.id}
          unitId={unitId}
          subunitId={subunitId}
          isCompleted={isCompleted}
          setIsCompleted={setIsCompleted}
        />
      ) : (
        <CourseProgressInfo course={course} units={units} />
      );
    }

    if (isOnStudentPages) {
      return activity.id ? (
        <StudentActivityInfo
          activity={activity}
          courseId={course.id}
          unitId={unitId}
          subunitId={subunitId}
          isCompleted={isCompleted}
          setIsCompleted={setIsCompleted}
        />
      ) : (
        <CourseProgressInfo course={course} units={units} />
      );
    }

    return null;
  };

  return (
    <nav
      className='navbar py-[32px] h-fit gap-4 flex flex-col border-t border-border_gray_300'
      style={showGradient ? UtilityService.getGradientStyle(darkMode) : undefined}
    >
      <div className='px-4 md:px-6 lg:px-12'>
        <div className='flex flex-row justify-between items-center'>
          <div className='flex flex-row gap-4 items-center'>
            {isMobile && !visible && (
              <div className='flex flex-row gap-4 h-0 mr-4 flex-shrink-0 xl:hidden'>
                <button
                  onClick={toggleSidenav}
                  className='p-2 h-fit rounded-md bg-background_white shadow-md border border-border_sidenav -translate-y-[50%]'
                  aria-label='Toggle navigation menu'
                >
                  <div className='w-6 h-6 flex flex-col justify-center items-center'>
                    <span className='block w-5 h-0.5 bg-text_gray_600 mb-1'></span>
                    <span className='block w-5 h-0.5 bg-text_gray_600 mb-1'></span>
                    <span className='block w-5 h-0.5 bg-text_gray_600'></span>
                  </div>
                </button>
              </div>
            )}
            <ul className='horizontal-list px-0 m-0 flex'>
              <BreadcrumbNav
                pathName={pathName}
                course={course}
                activity={activity}
                darkMode={darkMode}
                userRole={userRole}
              />
            </ul>
          </div>
          {isAdminCoursesRoot && course.id && <AdminStudentViewToggle />}
        </div>
        {renderContent()}
      </div>
    </nav>
  );
}

export default TopNav;
